import { LdSelectCustomEvent } from "@emdgroup-liquid/liquid";
import { LdOption } from "@liquid/react";
import { ReactNode } from "react";
import {
  FieldValues,
  Path,
  RegisterOptions,
  ValidationValueMessage,
  useController,
  useFormContext,
} from "react-hook-form";
import { OptionType } from "@src/types";
import { StyledLabel, StyledRequired, StyledSelect } from "./FormSelect.style";
import { ErrorMessage } from "../ErrorMessage";

type SelectProps<FormData extends FieldValues> = {
  name: Path<FormData>;
  options: Array<OptionType>;
  label?: ReactNode;
  tetherOptions?: {
    bodyElement: HTMLLdModalElement | null | undefined;
    constraints: { to: string }[];
  };
  disabled?: boolean;
  width?: number | string;
  placeholder?: string;
  rules?: Exclude<RegisterOptions, "valueAsNumber" | "setValueAs">;
  popperClass?: string;
  valueAs?: "string" | "number" | "boolean";
  filter?: boolean;
  onChange?:Function;
};

export const changeDataType = (
  e: LdSelectCustomEvent<string[]>,
  valueAs: SelectProps<any>["valueAs"]
) => {
  switch (valueAs) {
    case "number": {
      return e.detail.map((value) => Number(value));
    }
    case "boolean": {
      return e.detail.map((value) =>
        value.toLowerCase() === "true" ? true : false
      );
    }
    default: {
      return e.detail;
    }
  }
};

function FormSelect<FormData extends FieldValues>({
  options,
  name,
  label,
  tetherOptions,
  disabled = false,
  width,
  placeholder,
  rules,
  popperClass,
  valueAs,
  filter,
  onChange = () => {},
}: SelectProps<FormData>) {
  const {
    clearErrors,
    formState: { errors },
    control,
  } = useFormContext();

  const isRequired =
    rules?.required === true ||
    !!(rules?.required as ValidationValueMessage)?.value;

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  const handleChange = (e: LdSelectCustomEvent<string[]>) => {
    const values = changeDataType(e, valueAs);
    field.onChange(values.length === 1 ? values[0] : values);
    onChange(values[0]);
    clearErrors(name);
  };

  return (
    <StyledLabel>
      {label && (
        <StyledRequired data-required={isRequired}>{label}</StyledRequired>
      )}

      <StyledSelect
        {...field}
        key={options.length > 0 ? options[0]?.value : ""}
        width={width}
        invalid={!!errors[name]}
        onLdchange={handleChange}
        data-testid={`${name}-selectControlled-LdSelect`}
        tetherOptions={tetherOptions}
        prevent-deselection
        disabled={disabled}
        placeholder={placeholder}
        popperClass={popperClass}
        name={name}
        filter={filter}
      >
        {options.map((option, index) => (
          <LdOption
            key={option.value}
            value={option.value}
            selected={String(field.value) === option.value}
            data-testid={`${name}-select-LdOption`}
          >
            {option.label}
          </LdOption>
        ))}
      </StyledSelect>
      <ErrorMessage
        testId={`${name}-select-error-message-required`}
        errorMessage={error?.message}
      />
    </StyledLabel>
  );
}

export { FormSelect };
